import * as THREE from 'three';
import { RomanticCoupleAvatars } from './characterAvatars.js';

/**
 * Procedural 3D Character Vignettes for each of the 7 Days of Valentine
 */
export class DayCharactersManager {
  constructor() {
    this.scenes = {};
    this.initDayScenes();
  }

  initDayScenes() {
    const daysConfig = [
      { id: 'day-canvas-1', pose: 'rose', bg: 0xFFF5F2 },
      { id: 'day-canvas-2', pose: 'propose', bg: 0x1E0E12 },
      { id: 'day-canvas-3', pose: 'chocolate', bg: 0xFFF8F5 },
      { id: 'day-canvas-4', pose: 'teddy', bg: 0xFDF0EC },
      { id: 'day-canvas-5', pose: 'promise', bg: 0x180A0E },
      { id: 'day-canvas-6', pose: 'hug', bg: 0xFFF5F2 },
      { id: 'day-canvas-7', pose: 'celebrate', bg: 0xFAF0EE }
    ];

    daysConfig.forEach(cfg => {
      const container = document.getElementById(cfg.id);
      if (container) {
        this.scenes[cfg.pose] = this.createSingleDayScene(container, cfg);
      }
    });
  }

  createSingleDayScene(container, cfg) {
    const width = container.clientWidth || 420;
    const height = container.clientHeight || 480;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(36, width / height, 0.1, 50);
    if (cfg.pose === 'hug') {
      camera.position.set(0, 0.95, 5.5);
      camera.lookAt(0, 0.45, 0);
    } else {
      camera.position.set(0, 1.25, 5.6);
      camera.lookAt(0, 0.55, 0);
    }

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);

    // Soft Studio Lighting
    const ambientLight = new THREE.AmbientLight(0xFFEFE8, 1.3);
    scene.add(ambientLight);

    const spotLight = new THREE.DirectionalLight(0xFFE8DD, 2.2);
    spotLight.position.set(3, 5, 4);
    spotLight.castShadow = true;
    scene.add(spotLight);

    const fillLight = new THREE.DirectionalLight(0xFFCCD5, 0.9);
    fillLight.position.set(-3, 2, 2);
    scene.add(fillLight);

    // Marble Pedestal
    const pedGeo = new THREE.CylinderGeometry(1.6, 1.7, 0.3, 32);
    const pedMat = new THREE.MeshStandardMaterial({
      color: cfg.bg === 0x1E0E12 || cfg.bg === 0x180A0E ? 0x2A141A : 0xFAF6F0,
      roughness: 0.4
    });
    const pedestal = new THREE.Mesh(pedGeo, pedMat);
    pedestal.position.y = -0.15;
    pedestal.receiveShadow = true;
    scene.add(pedestal);

    // Couple Avatar
    const couple = new RomanticCoupleAvatars();
    couple.root.position.y = 0;
    if (cfg.pose === 'hug') {
      couple.root.scale.set(0.78, 0.78, 0.78);
    } else {
      couple.root.scale.set(0.9, 0.9, 0.9);
    }

    this.applyDayPose(couple, cfg.pose, scene);
    scene.add(couple.root);

    // Continuous 3D Falling Particles (Rose Petal Rain or Chocolate Rain)
    const particles = [];
    if (cfg.pose === 'rose') {
      const petalGeo = new THREE.SphereGeometry(0.09, 8, 6);
      petalGeo.scale(1.2, 0.28, 0.85);
      const petalColors = [0xC71F37, 0xE63946, 0xFF4D6D, 0xF4A8A1, 0xD62828];

      for (let i = 0; i < 45; i++) {
        const pMat = new THREE.MeshStandardMaterial({
          color: petalColors[i % petalColors.length],
          roughness: 0.5,
          metalness: 0.1,
          transparent: true,
          opacity: 0.88,
          side: THREE.DoubleSide
        });
        const petal = new THREE.Mesh(petalGeo, pMat);
        petal.position.set(
          (Math.random() - 0.5) * 5.0,
          Math.random() * 6.0 - 2.0,
          (Math.random() - 0.5) * 3.5
        );
        petal.rotation.set(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2
        );
        petal.userData = {
          speedY: Math.random() * 0.018 + 0.012,
          rotX: Math.random() * 0.03 - 0.015,
          rotY: Math.random() * 0.03 - 0.015,
          rotZ: Math.random() * 0.02 - 0.01,
          driftX: Math.random() * 0.008 - 0.004,
          initX: petal.position.x
        };
        scene.add(petal);
        particles.push(petal);
      }
    } else if (cfg.pose === 'promise') {
      // Golden Celestial Starlight Floating Sparkles for Dance
      const starGeo = new THREE.OctahedronGeometry(0.045, 0);
      const starColors = [0xFFDF73, 0xD4AF37, 0xFFE599, 0xFFFFFF];

      for (let i = 0; i < 35; i++) {
        const sMat = new THREE.MeshStandardMaterial({
          color: starColors[i % starColors.length],
          emissive: starColors[i % starColors.length],
          emissiveIntensity: 0.6,
          roughness: 0.2,
          metalness: 0.9,
          transparent: true,
          opacity: 0.85
        });
        const star = new THREE.Mesh(starGeo, sMat);
        star.position.set(
          (Math.random() - 0.5) * 4.5,
          Math.random() * 4.5 - 1.0,
          (Math.random() - 0.5) * 3.5
        );
        star.userData = {
          speedY: Math.random() * 0.008 + 0.004,
          rotX: Math.random() * 0.04 - 0.02,
          rotY: Math.random() * 0.04 - 0.02,
          rotZ: Math.random() * 0.03 - 0.015,
          initX: star.position.x
        };
        scene.add(star);
        particles.push(star);
      }
    } else if (cfg.pose === 'hug') {
      // Floating Intimacy Glowing Hearts for Hug & Kiss Day
      const heartGeo = new THREE.SphereGeometry(0.065, 10, 8);
      heartGeo.scale(1.2, 0.95, 0.55);
      const heartColors = [0xFF4D6D, 0xFF758F, 0xC71F37, 0xFFB3C1];

      for (let i = 0; i < 30; i++) {
        const hMat = new THREE.MeshStandardMaterial({
          color: heartColors[i % heartColors.length],
          emissive: heartColors[i % heartColors.length],
          emissiveIntensity: 0.45,
          roughness: 0.3,
          transparent: true,
          opacity: 0.85
        });
        const hMesh = new THREE.Mesh(heartGeo, hMat);
        hMesh.position.set(
          (Math.random() - 0.5) * 4.5,
          Math.random() * 4.5 - 1.0,
          (Math.random() - 0.5) * 3.5
        );
        hMesh.userData = {
          speedY: Math.random() * 0.010 + 0.005,
          rotX: Math.random() * 0.03 - 0.015,
          rotY: Math.random() * 0.03 - 0.015,
          rotZ: Math.random() * 0.02 - 0.01,
          initX: hMesh.position.x
        };
        scene.add(hMesh);
        particles.push(hMesh);
      }
    }

    // Mouse Tracking
    const mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };
    container.addEventListener('mousemove', (e) => {
      const rect = container.getBoundingClientRect();
      mouse.targetX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.targetY = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    });

    const clock = new THREE.Clock();

    const sceneHandle = {
      scene,
      camera,
      renderer,
      couple,
      pedestal,
      scrollProgress: 0,
      setScroll: (p) => {
        sceneHandle.scrollProgress = p;
      }
    };

    const animate = () => {
      requestAnimationFrame(animate);
      const delta = clock.getDelta();
      const t = clock.getElapsedTime();

      mouse.x += (mouse.targetX - mouse.x) * 0.08;
      mouse.y += (mouse.targetY - mouse.y) * 0.08;

      camera.position.x = mouse.x * 0.35;
      camera.lookAt(0, 0.9 + mouse.y * 0.12, 0);

      // Dedicated Scroll-Driven Kinematics for Day 1 (Rose Day)
      if (cfg.pose === 'rose') {
        const p = sceneHandle.scrollProgress;

        if (p < 0.45) {
          // Phase 1: Boy steps forward towards the Girl
          const walkNorm = p / 0.45;
          couple.boy.position.x = -1.3 + walkNorm * 0.85; // -1.3 -> -0.45
          couple.boy.rotation.y = 0.55;

          // Leg walking rotation
          const stepSwing = Math.sin(walkNorm * Math.PI * 6) * 0.35;
          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = stepSwing;
            couple.boy.children[4].rotation.x = -stepSwing;
          }

          // Arm holding rose close in front of chest
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.25, 0, -0.2);
          }
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.1, 0, 0.1);
          }
          if (couple.sharedHeart) {
            couple.sharedHeart.scale.set(0, 0, 0);
          }
        } else if (p < 0.8) {
          // Phase 2: Boy reaches girl, stops walking, and smoothly extends the rose forward
          const offerNorm = (p - 0.45) / 0.35; // 0 -> 1
          couple.boy.position.x = -0.45;
          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = 0;
            couple.boy.children[4].rotation.x = 0;
          }

          // Arm extending forward in front of chest with the rose
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.25 - offerNorm * 0.55, 0, -0.2 - offerNorm * 0.3);
          }
          // Girl's hands reach forward to receive
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-offerNorm * 0.65, 0, offerNorm * 0.35);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-offerNorm * 0.6, 0, -offerNorm * 0.25);
          }

          // Camera zooms closer for the romantic climax
          camera.position.z = 5.6 - offerNorm * 0.7;
          camera.position.y = 1.25 - offerNorm * 0.15;
        } else {
          // Phase 3: Rose Handover Complete! Girl holds rose, heart blossoms!
          const completeNorm = (p - 0.8) / 0.2; // 0 -> 1
          couple.boy.position.x = -0.45;
          couple.boy.userData.rightArm.rotation.set(-0.8, 0, -0.5);

          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.7, 0, 0.35);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.65, 0, -0.25);
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.set(0.1, -0.45, 0.1);
          }

          // Shared Heart bursts open
          if (couple.sharedHeart) {
            const hScale = Math.min(1.3, 0.5 + completeNorm * 0.8);
            couple.sharedHeart.scale.set(hScale, hScale, hScale);
          }
        }
      } else if (cfg.pose === 'propose') {
        // Continuous 360° Round-and-Round Turntable Rotation for Proposal Vignette
        couple.root.rotation.y = t * 0.45;

        // Subtle emotional breathing
        if (couple.girl.userData.head) {
          couple.girl.userData.head.rotation.x = 0.12 + Math.sin(t * 2.0) * 0.03;
        }
        if (couple.boy.userData.rightArm) {
          couple.boy.userData.rightArm.rotation.x = -0.75 + Math.sin(t * 2.5) * 0.03;
        }
      } else if (cfg.pose === 'teddy') {
        // Dedicated Scroll-Driven Kinematics: Aarav walks with Big Pink Teddy Bear, drops to one knee, & offers it to Meera!
        const p = sceneHandle.scrollProgress;

        if (p < 0.45) {
          // Phase 1: Aarav walks forward holding the Big Pink Teddy Bear
          const walkNorm = p / 0.45;
          couple.boy.position.x = -1.35 + walkNorm * 0.87; // -1.35 -> -0.48
          couple.boy.position.y = 0;
          couple.boy.rotation.set(0, 0.55, Math.sin(walkNorm * Math.PI * 6) * 0.06);

          // Leg walking rotation
          const stepSwing = Math.sin(walkNorm * Math.PI * 6) * 0.35;
          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = stepSwing;
            couple.boy.children[4].rotation.x = -stepSwing;
            couple.boy.children[4].position.y = 0;
          }

          // Holding big teddy comfortably at chest level while walking
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.38, 0, -0.2);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.38, 0, 0.2);
          }

          // Teddy moves along with Aarav at chest height
          if (couple.teddyProp) {
            couple.teddyProp.position.set(couple.boy.position.x + 0.28, 0.48 + Math.abs(Math.sin(walkNorm * Math.PI * 6)) * 0.03, 0.28);
            couple.teddyProp.rotation.set(0, 0.2, 0);
          }

          // Meera watches happily
          couple.girl.position.set(0.48, 0, 0);
          couple.girl.rotation.set(0, -0.55, 0);
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.2, 0.1, 0.15);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.2, -0.1, -0.15);
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.set(0.05, -0.45, 0);
          }
        } else if (p < 0.8) {
          // Phase 2: Aarav drops on one knee and extends the Big Pink Teddy Bear forward
          const offerNorm = (p - 0.45) / 0.35; // 0 -> 1
          couple.boy.position.x = -0.48;
          couple.boy.position.y = -offerNorm * 0.26;
          couple.boy.rotation.set(0, 0.55, 0);

          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = -offerNorm * 1.1; // front bent knee
            couple.boy.children[4].rotation.x = offerNorm * 0.9;  // back resting knee
            couple.boy.children[4].position.y = offerNorm * 0.2;
          }

          // Arms extending forward presenting the big pink teddy
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.38 - offerNorm * 0.30, 0, -0.2 - offerNorm * 0.15);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.38 - offerNorm * 0.30, 0, 0.2 + offerNorm * 0.15);
          }

          // Teddy pushed forward toward Meera at gentle waist/chest height
          if (couple.teddyProp) {
            couple.teddyProp.position.set(-0.48 + 0.32 + offerNorm * 0.14, 0.48 - offerNorm * 0.12, 0.28 + offerNorm * 0.08);
            couple.teddyProp.rotation.set(0, 0.2 - offerNorm * 0.15, 0);
          }

          // Girl brings hands to cheeks in surprise & adoration
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.2 - offerNorm * 0.65, 0.15, 0.15 + offerNorm * 0.25);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.2 - offerNorm * 0.65, -0.15, -0.15 - offerNorm * 0.25);
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.set(offerNorm * 0.15, -0.45, offerNorm * 0.1);
          }
        } else {
          // Phase 3: Girl steps in to accept and hug the Big Pink Teddy Bear!
          const completeNorm = (p - 0.8) / 0.2;
          couple.boy.position.x = -0.48;
          couple.boy.position.y = -0.26;
          couple.boy.rotation.set(0, 0.55, 0);

          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = -1.1;
            couple.boy.children[4].rotation.x = 0.9;
            couple.boy.children[4].position.y = 0.2;
          }

          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.68, 0, -0.35);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.68, 0, 0.35);
          }

          couple.girl.position.x = 0.48 - completeNorm * 0.14; // steps in closer
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.75, 0.2, 0.35);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.75, -0.2, -0.35);
          }

          if (couple.teddyProp) {
            couple.teddyProp.position.set(-0.02, 0.36 + Math.sin(t * 2.0) * 0.015, 0.32);
            couple.teddyProp.rotation.set(0, 0.05, 0);
          }
        }
      } else if (cfg.pose === 'promise') {
        // Romantic Collision-Free Couple Ballroom Waltz & Dance Animation
        const danceSpeed = 1.35;
        const swayRoll = Math.sin(t * danceSpeed) * 0.05;
        const bounce = Math.abs(Math.sin(t * danceSpeed * 2.0)) * 0.025;

        // Circular ballroom dance floor rotation as a single harmonious unit
        const danceRotation = t * 0.35;
        couple.root.position.set(0, bounce, 0);
        couple.root.rotation.y = danceRotation;
        couple.root.rotation.z = swayRoll;

        // Fixed comfortable distance preventing any mesh collision
        couple.boy.position.set(-0.46, 0, 0);
        couple.boy.rotation.set(0, 0.45, 0);

        couple.girl.position.set(0.46, 0, 0);
        couple.girl.rotation.set(0, -0.45, 0);

        // Loving eye contact
        if (couple.boy.userData.head) {
          couple.boy.userData.head.rotation.set(0.04, 0.25, Math.sin(t * danceSpeed) * 0.02);
        }
        if (couple.girl.userData.head) {
          couple.girl.userData.head.rotation.set(0.04, -0.25, -Math.sin(t * danceSpeed) * 0.02);
        }

        // Extended ballroom hand-hold
        if (couple.boy.userData.leftArm) {
          couple.boy.userData.leftArm.rotation.set(-0.75 + Math.sin(t * danceSpeed) * 0.04, 0, 0.62);
        }
        if (couple.girl.userData.rightArm) {
          couple.girl.userData.rightArm.rotation.set(-0.75 + Math.sin(t * danceSpeed) * 0.04, 0, -0.62);
        }
        if (couple.boy.userData.rightArm) {
          couple.boy.userData.rightArm.rotation.set(-0.35, 0, -0.45);
        }
        if (couple.girl.userData.leftArm) {
          couple.girl.userData.leftArm.rotation.set(-0.35, 0.1, 0.45);
        }

        // Synchronized rhythmic footwork
        if (couple.boy.children[3] && couple.boy.children[4]) {
          couple.boy.children[3].rotation.x = Math.sin(t * danceSpeed) * 0.18;
          couple.boy.children[4].rotation.x = -Math.sin(t * danceSpeed) * 0.18;
        }
        if (couple.girl.children[3] && couple.girl.children[4]) {
          couple.girl.children[3].rotation.x = -Math.sin(t * danceSpeed) * 0.18;
          couple.girl.children[4].rotation.x = Math.sin(t * danceSpeed) * 0.18;
        }
      } else if (cfg.pose === 'hug') {
        const p = sceneHandle.scrollProgress;
        const bike = couple.scooterProp;

        if (p < 0.35) {
          // Phase 1: Aarav rides the authentic classic motorcycle onto the stage!
          const rideNorm = p / 0.35; // 0 -> 1
          const posX = -2.6 + rideNorm * 1.65; // -2.6 -> -0.95
          const engineVibe = Math.sin(t * 24.0) * 0.012;

          if (bike) {
            bike.position.set(posX, 0, 0);
            bike.rotation.set(0, 0.1, Math.sin(rideNorm * Math.PI * 4) * 0.02);
            if (bike.userData.frontWheel) bike.userData.frontWheel.rotation.z -= 0.35;
            if (bike.userData.backWheel) bike.userData.backWheel.rotation.z -= 0.35;
          }

          // Aarav sitting properly squarely on the brown leather seat facing SIDEWAYS (+X towards Meera)
          const boyRidingX = (posX - 0.20) / 0.78;
          couple.boy.position.set(boyRidingX, 0.32 + engineVibe, 0);
          couple.boy.rotation.set(0, Math.PI / 2, 0.12);

          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = -1.25; // sitting legs bent on footpegs
            couple.boy.children[4].rotation.x = -1.25;
            couple.boy.children[4].position.y = 0;
          }
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-1.20, 0, -0.16);
            couple.boy.userData.rightArm.scale.set(1.0, 1.25, 1.0);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-1.20, 0, 0.16);
            couple.boy.userData.leftArm.scale.set(1.0, 1.25, 1.0);
          }
          if (couple.boy.userData.head) {
            couple.boy.userData.head.rotation.set(0.06, 0, 0);
          }

          // Meera stands and waves eagerly
          couple.girl.position.set(0.42, 0, 0);
          couple.girl.rotation.set(0, -0.55, 0);
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.85 + Math.sin(t * 6.0) * 0.25, 0, -0.6);
          }
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.2, 0.1, 0.2);
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.set(0.04, -0.45, 0);
          }

          if (couple.kissShieldHeart) {
            couple.kissShieldHeart.scale.set(0.0001, 0.0001, 0.0001);
          }
        } else if (p < 0.65) {
          // Phase 2: Aarav parks bike, hops off and walks across to Meera
          const walkNorm = (p - 0.35) / 0.30; // 0 -> 1
          if (bike) {
            bike.position.set(-0.95, 0, 0);
            bike.rotation.set(0, 0.15, -0.08); // parked on kickstand
          }

          const boyStartX = (-0.95 - 0.20) / 0.78; // -1.474 (saddle position)
          const boyX = boyStartX + walkNorm * (-0.24 - boyStartX);
          const stepSwing = Math.sin(walkNorm * Math.PI * 6) * 0.35;
          const turnAngle = Math.PI / 2 - walkNorm * (Math.PI / 2 - 0.55);

          couple.boy.position.set(boyX, Math.abs(Math.sin(walkNorm * Math.PI * 6)) * 0.03, 0.02);
          couple.boy.rotation.set(0, turnAngle, 0);

          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = stepSwing;
            couple.boy.children[4].rotation.x = -stepSwing;
            couple.boy.children[4].position.y = 0;
          }

          // Aarav opens arms ready to embrace
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.4 - walkNorm * 0.35, 0.1, -0.4 - walkNorm * 0.45);
            couple.boy.userData.rightArm.scale.set(1.0, 1.0, 1.0);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.35 - walkNorm * 0.2, -0.1, 0.3 + walkNorm * 0.25);
            couple.boy.userData.leftArm.scale.set(1.0, 1.0, 1.0);
          }

          // Meera opens arms too
          couple.girl.position.set(0.42 - walkNorm * 0.18, 0, 0.02); // 0.42 -> 0.24
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.4 - walkNorm * 0.35, -0.1, 0.4 + walkNorm * 0.45);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.35 - walkNorm * 0.2, 0.1, -0.3 - walkNorm * 0.25);
          }

          if (couple.kissShieldHeart) {
            couple.kissShieldHeart.scale.set(0.0001, 0.0001, 0.0001);
          }
        } else if (p < 0.85) {
          // Phase 3: Warm Tight Hug!
          const hugNorm = (p - 0.65) / 0.20;
          const breath = Math.sin(t * 2.0) * 0.012;

          if (bike) {
            bike.position.set(-0.95, 0, 0);
            bike.rotation.set(0, 0.15, -0.08);
          }

          couple.boy.position.set(-0.24, 0, 0.02);
          couple.boy.rotation.set(0.04, 0.55, 0);

          if (couple.boy.children[3] && couple.boy.children[4]) {
            couple.boy.children[3].rotation.x = 0;
            couple.boy.children[4].rotation.x = 0;
          }

          couple.girl.position.set(0.24, 0, 0.02);
          couple.girl.rotation.set(0.04, -0.55, 0);

          // Arms wrapped tight around each other
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.75, 0.2, -0.85);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.55, -0.1, 0.55);
          }
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.75, -0.2, 0.85);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.55, 0.1, -0.55);
          }

          if (couple.boy.userData.head) {
            couple.boy.userData.head.rotation.set(0.12 + breath, 0.52, -0.04);
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.set(0.12 + breath, -0.52, 0.04);
          }

          if (couple.kissShieldHeart) {
            couple.kissShieldHeart.scale.set(0.0001, 0.0001, 0.0001);
          }
        } else {
          // Phase 4: Forehead / Cheek Kiss with 3D Glowing Kiss Shield Heart!
          const kissNorm = (p - 0.85) / 0.15; // 0 -> 1
          const breath = Math.sin(t * 1.8) * 0.01;

          if (bike) {
            bike.position.set(-0.95, 0, 0);
            bike.rotation.set(0, 0.15, -0.08);
          }

          couple.boy.position.set(-0.20, 0, 0.02);
          couple.boy.rotation.set(0.04, 0.55, 0);

          couple.girl.position.set(0.20, 0, 0.02);
          couple.girl.rotation.set(0.04, -0.55, 0);

          // Intimate forehead / cheek kiss lean
          if (couple.boy.userData.head) {
            couple.boy.userData.head.rotation.set(0.28 + breath, 0.62, -0.06);
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.set(0.26 + breath, -0.62, 0.06);
          }

          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.75, 0.2, -0.85);
          }
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.60, -0.1, 0.60);
          }
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.75, -0.2, 0.85);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.60, 0.1, -0.60);
          }

          // Pop up glowing romantic 3D heart right in front of their faces to playfully shield the kiss!
          if (couple.kissShieldHeart) {
            const heartPulse = 1.0 + Math.sin(t * 7.0) * 0.08;
            const popScale = Math.min(1.0, kissNorm * 1.4) * 0.48 * heartPulse;
            couple.kissShieldHeart.scale.set(popScale, popScale, popScale);
            couple.kissShieldHeart.position.set(0, 1.04 + Math.sin(t * 3.0) * 0.015, 0.62);
            couple.kissShieldHeart.rotation.set(0, 0, Math.sin(t * 2.5) * 0.08);
          }
        }
      } else if (cfg.pose === 'celebrate') {
        if (couple.diamondMesh) {
          couple.diamondMesh.rotation.y += 0.04;
        }

        if (!sceneHandle.isCelebrated) {
          // Devoted Proposal Anticipation Pose
          const breath = Math.sin(t * 2.0) * 0.012;
          if (couple.boy.userData.head) {
            couple.boy.userData.head.rotation.x = -0.15 + breath;
          }
          if (couple.girl.userData.head) {
            couple.girl.userData.head.rotation.x = 0.18 + breath;
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-1.52 + Math.sin(t * 2.0) * 0.02, 0.22, -0.28);
          }
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-1.52 + Math.sin(t * 2.0) * 0.02, -0.22, 0.28);
          }
        } else {
          // Grand Celebration: Aarav stands up, and they celebrate with joyous waving & spinning!
          const celTime = sceneHandle.celebrationTime || 0;
          sceneHandle.celebrationTime = celTime + delta;

          // Aarav smoothly rises from kneel
          if (couple.kneelingLegs) {
            couple.kneelingLegs.visible = false;
            if (couple.boy.children[3]) couple.boy.children[3].visible = true;
            if (couple.boy.children[4]) couple.boy.children[4].visible = true;
          }
          if (couple.boy.position.y < 0) {
            couple.boy.position.y = Math.min(0, couple.boy.position.y + delta * 0.7);
            if (couple.boy.children[3]) couple.boy.children[3].rotation.x = 0;
            if (couple.boy.children[4]) couple.boy.children[4].rotation.x = 0;
          }

          // Joyful celebration arms waving
          if (couple.boy.userData.leftArm) {
            couple.boy.userData.leftArm.rotation.set(-0.8 + Math.sin(t * 6.0) * 0.35, 0, 0.9);
          }
          if (couple.boy.userData.rightArm) {
            couple.boy.userData.rightArm.rotation.set(-0.8 + Math.cos(t * 6.0) * 0.35, 0, -0.9);
          }
          if (couple.girl.userData.leftArm) {
            couple.girl.userData.leftArm.rotation.set(-0.8 + Math.cos(t * 6.0) * 0.35, 0, 0.9);
          }
          if (couple.girl.userData.rightArm) {
            couple.girl.userData.rightArm.rotation.set(-0.8 + Math.sin(t * 6.0) * 0.35, 0, -0.9);
          }

          // Pedestal & couple spin
          pedestal.rotation.y += 0.018;
          couple.root.rotation.y += 0.018;
        }
      } else {
        couple.update(delta, 1.0, mouse.x, mouse.y);
      }

      // Continuous Falling 3D Particles Animation (Rose Petals or Chocolates Rain)
      for (let i = 0; i < particles.length; i++) {
        const pt = particles[i];
        pt.position.y -= pt.userData.speedY;
        pt.rotation.x += pt.userData.rotX;
        pt.rotation.y += pt.userData.rotY;
        pt.rotation.z += pt.userData.rotZ;
        pt.position.x = pt.userData.initX + Math.sin(t * 1.5 + i) * 0.35;

        // Loop / recycle when particle reaches bottom
        if (pt.position.y < -2.4) {
          pt.position.y = 3.5;
          pt.position.x = (Math.random() - 0.5) * 5.0;
          pt.userData.initX = pt.position.x;
        }
      }

      // Gentle pedestal sway
      pedestal.rotation.y = Math.sin(t * 0.4) * 0.03;

      renderer.render(scene, camera);
    };
    animate();

    window.addEventListener('resize', () => {
      const newW = container.clientWidth || 420;
      const newH = container.clientHeight || 480;
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
      renderer.setSize(newW, newH);
    });

    return sceneHandle;
  }

  setRoseDayProgress(p) {
    if (this.scenes['rose']) {
      this.scenes['rose'].setScroll(p);
    }
  }

  setTeddyDayProgress(p) {
    if (this.scenes['teddy']) {
      this.scenes['teddy'].setScroll(p);
    }
  }

  setHugDayProgress(p) {
    if (this.scenes['hug']) {
      this.scenes['hug'].setScroll(p);
    }
  }

  applyDayPose(couple, pose, scene) {
    if (pose === 'rose') {
      couple.boy.position.set(-0.45, 0, 0);
      couple.boy.rotation.set(0, 0.55, 0);
      couple.girl.position.set(0.45, 0, 0);
      couple.girl.rotation.set(0, -0.55, 0);
      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-0.75, 0, -0.2);
      }
      if (couple.girl.userData.leftArm) {
        couple.girl.userData.leftArm.rotation.set(-0.55, 0.2, 0.3);
      }
    } else if (pose === 'propose') {
      couple.boy.position.set(-0.45, -0.25, 0);
      couple.boy.rotation.set(0, 0.55, 0);
      couple.girl.position.set(0.45, 0, 0);
      couple.girl.rotation.set(0, -0.55, 0);
      if (couple.boy.children[3] && couple.boy.children[4]) {
        couple.boy.children[3].rotation.x = -1.1;
        couple.boy.children[4].rotation.x = 0.9;
        couple.boy.children[4].position.y = 0.2;
      }
      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-0.95, 0, -0.3);
      }
      if (couple.girl.userData.leftArm) {
        couple.girl.userData.leftArm.rotation.set(-0.85, 0.2, 0.35);
      }
      if (couple.girl.userData.rightArm) {
        couple.girl.userData.rightArm.rotation.set(-0.85, -0.2, -0.35);
      }
    } else if (pose === 'chocolate') {
      couple.boy.position.set(-0.45, 0, 0);
      couple.boy.rotation.set(0, 0.45, 0);
      couple.girl.position.set(0.45, 0, 0);
      couple.girl.rotation.set(0, -0.45, 0);

      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-0.9, -0.15, 0.3);
      }
      if (couple.girl.userData.rightArm) {
        couple.girl.userData.rightArm.rotation.set(-0.9, 0.15, -0.3);
      }

      // Detailed Luxury Artisan Chocolate Box
      const chocoGroup = new THREE.Group();
      const boxGeo = new THREE.BoxGeometry(0.38, 0.12, 0.28);
      const boxMat = new THREE.MeshStandardMaterial({ color: 0x500810, roughness: 0.35 });
      const chocoBox = new THREE.Mesh(boxGeo, boxMat);
      chocoGroup.add(chocoBox);

      // Gold ribbon border
      const ribbonGeo = new THREE.BoxGeometry(0.39, 0.03, 0.29);
      const ribbonMat = new THREE.MeshStandardMaterial({ color: 0xD4AF37, metalness: 0.85, roughness: 0.15 });
      const ribbon = new THREE.Mesh(ribbonGeo, ribbonMat);
      ribbon.position.y = 0.02;
      chocoGroup.add(ribbon);

      // Assorted truffles inside box
      const truffMat1 = new THREE.MeshStandardMaterial({ color: 0x38190B, roughness: 0.2 });
      const truffMat2 = new THREE.MeshStandardMaterial({ color: 0xD4AF37, metalness: 0.9, roughness: 0.1 });
      const truffMat3 = new THREE.MeshStandardMaterial({ color: 0x8E1616, roughness: 0.25 });

      const tGeo1 = new THREE.SphereGeometry(0.04, 8, 6);
      const tGeo2 = new THREE.BoxGeometry(0.065, 0.05, 0.065);

      const t1 = new THREE.Mesh(tGeo1, truffMat1);
      t1.position.set(-0.1, 0.07, -0.06);
      chocoGroup.add(t1);

      const t2 = new THREE.Mesh(tGeo2, truffMat2);
      t2.position.set(0.1, 0.07, -0.06);
      chocoGroup.add(t2);

      const t3 = new THREE.Mesh(tGeo1, truffMat3);
      t3.position.set(0, 0.07, 0.06);
      chocoGroup.add(t3);

      chocoGroup.position.set(0, 0.9, 0.25);
      scene.add(chocoGroup);
    } else if (pose === 'teddy') {
      // Day 4: Big Cuddly Pink Teddy Bear held in character's hands
      if (couple.boy.userData.rose) {
        couple.boy.userData.rose.visible = false;
      }

      couple.boy.position.set(-0.48, 0, 0);
      couple.boy.rotation.set(0, 0.55, 0);
      couple.girl.position.set(0.48, 0, 0);
      couple.girl.rotation.set(0, -0.55, 0);

      // Arm Poses holding the big teddy
      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-0.65, 0, -0.25);
      }
      if (couple.boy.userData.leftArm) {
        couple.boy.userData.leftArm.rotation.set(-0.65, 0, 0.25);
      }
      if (couple.girl.userData.leftArm) {
        couple.girl.userData.leftArm.rotation.set(-0.45, 0.2, 0.35);
      }
      if (couple.girl.userData.rightArm) {
        couple.girl.userData.rightArm.rotation.set(-0.45, -0.2, -0.35);
      }

      // Build Big Plush Pink Teddy Bear
      const teddy = new THREE.Group();

      const pinkFurMat = new THREE.MeshStandardMaterial({
        color: 0xFF6B8B, // Vibrant cute plush rose pink
        roughness: 0.88,
        metalness: 0.05
      });

      const pinkInnerMat = new THREE.MeshStandardMaterial({
        color: 0xFFC2D1, // Soft pastel inner pink
        roughness: 0.82
      });

      const eyeMat = new THREE.MeshStandardMaterial({
        color: 0x1A0A0E,
        roughness: 0.15,
        metalness: 0.85
      });

      const noseMat = new THREE.MeshStandardMaterial({
        color: 0x3D131D,
        roughness: 0.35
      });

      const bowMat = new THREE.MeshStandardMaterial({
        color: 0xC71F37,
        roughness: 0.3,
        metalness: 0.2
      });

      // 1. Chubby Pink Body
      const bodyGeo = new THREE.SphereGeometry(0.24, 16, 12);
      bodyGeo.scale(1.05, 1.18, 0.95);
      const body = new THREE.Mesh(bodyGeo, pinkFurMat);
      teddy.add(body);

      // Soft Light Pink Tummy Patch
      const tummyGeo = new THREE.SphereGeometry(0.16, 14, 10);
      tummyGeo.scale(0.95, 1.05, 0.35);
      const tummy = new THREE.Mesh(tummyGeo, pinkInnerMat);
      tummy.position.set(0, 0, 0.19);
      teddy.add(tummy);

      // 2. Big Cute Round Head
      const headGeo = new THREE.SphereGeometry(0.21, 16, 14);
      const head = new THREE.Mesh(headGeo, pinkFurMat);
      head.position.set(0, 0.34, 0.04);
      teddy.add(head);

      // Snout
      const snoutGeo = new THREE.SphereGeometry(0.095, 12, 10);
      snoutGeo.scale(1.15, 0.85, 0.9);
      const snout = new THREE.Mesh(snoutGeo, pinkInnerMat);
      snout.position.set(0, 0.30, 0.20);
      teddy.add(snout);

      // Button Nose
      const noseGeo = new THREE.SphereGeometry(0.038, 10, 8);
      noseGeo.scale(1.2, 0.8, 0.8);
      const nose = new THREE.Mesh(noseGeo, noseMat);
      nose.position.set(0, 0.34, 0.27);
      teddy.add(nose);

      // Shiny Beaded Eyes
      const eyeGeo = new THREE.SphereGeometry(0.026, 8, 8);
      const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
      leftEye.position.set(-0.085, 0.38, 0.20);
      const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
      rightEye.position.set(0.085, 0.38, 0.20);
      teddy.add(leftEye);
      teddy.add(rightEye);

      // Round Fluffy Ears
      const earGeo = new THREE.SphereGeometry(0.09, 12, 10);
      const innerEarGeo = new THREE.SphereGeometry(0.052, 10, 8);

      const leftEar = new THREE.Mesh(earGeo, pinkFurMat);
      leftEar.position.set(-0.17, 0.50, 0.02);
      const leftInner = new THREE.Mesh(innerEarGeo, pinkInnerMat);
      leftInner.position.set(-0.17, 0.50, 0.06);

      const rightEar = new THREE.Mesh(earGeo, pinkFurMat);
      rightEar.position.set(0.17, 0.50, 0.02);
      const rightInner = new THREE.Mesh(innerEarGeo, pinkInnerMat);
      rightInner.position.set(0.17, 0.50, 0.06);

      teddy.add(leftEar);
      teddy.add(leftInner);
      teddy.add(rightEar);
      teddy.add(rightInner);

      // 3. Satin Velvet Crimson Bow around Teddy's Neck
      const bowCenter = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 8), bowMat);
      bowCenter.position.set(0, 0.19, 0.21);
      const bowLeft = new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 8), bowMat);
      bowLeft.scale.set(1.4, 0.7, 0.5);
      bowLeft.position.set(-0.07, 0.19, 0.20);
      bowLeft.rotation.z = 0.25;
      const bowRight = new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 8), bowMat);
      bowRight.scale.set(1.4, 0.7, 0.5);
      bowRight.position.set(0.07, 0.19, 0.20);
      bowRight.rotation.z = -0.25;

      teddy.add(bowCenter);
      teddy.add(bowLeft);
      teddy.add(bowRight);

      // 4. Cute Little Paws holding a Valentine Love Heart
      const pawGeo = new THREE.CylinderGeometry(0.06, 0.07, 0.20, 10);
      const leftPaw = new THREE.Mesh(pawGeo, pinkFurMat);
      leftPaw.position.set(-0.21, 0.06, 0.13);
      leftPaw.rotation.set(0.7, 0, -0.4);
      const rightPaw = new THREE.Mesh(pawGeo, pinkFurMat);
      rightPaw.position.set(0.21, 0.06, 0.13);
      rightPaw.rotation.set(0.7, 0, 0.4);
      teddy.add(leftPaw);
      teddy.add(rightPaw);

      // Mini Velvet Heart in Teddy's Paws
      const miniHeartGeo = new THREE.SphereGeometry(0.08, 10, 8);
      miniHeartGeo.scale(1.2, 0.95, 0.6);
      const miniHeart = new THREE.Mesh(miniHeartGeo, bowMat);
      miniHeart.position.set(0, 0.05, 0.25);
      teddy.add(miniHeart);

      // 5. Chubby Pink Feet
      const footGeo = new THREE.SphereGeometry(0.095, 10, 8);
      footGeo.scale(1.0, 0.75, 1.35);
      const leftFoot = new THREE.Mesh(footGeo, pinkFurMat);
      leftFoot.position.set(-0.15, -0.24, 0.12);
      const rightFoot = new THREE.Mesh(footGeo, pinkFurMat);
      rightFoot.position.set(0.15, -0.24, 0.12);
      teddy.add(leftFoot);
      teddy.add(rightFoot);

      // Scale and position big teddy in hands in front of couple
      teddy.scale.set(1.0, 1.0, 1.0);
      teddy.position.set(-0.02, 0.45, 0.28);
      scene.add(teddy);
      couple.teddyProp = teddy;
    } else if (pose === 'promise') {
      // Day 5: Romantic Ballroom Dance Pose
      if (couple.boy.userData.rose) {
        couple.boy.userData.rose.visible = false;
      }
      couple.boy.position.set(-0.46, 0, 0);
      couple.boy.rotation.set(0, 0.45, 0);
      couple.girl.position.set(0.46, 0, 0);
      couple.girl.rotation.set(0, -0.45, 0);

      if (couple.boy.userData.leftArm) {
        couple.boy.userData.leftArm.rotation.set(-0.75, 0, 0.62);
      }
      if (couple.girl.userData.rightArm) {
        couple.girl.userData.rightArm.rotation.set(-0.75, 0, -0.62);
      }
      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-0.35, 0, -0.45);
      }
      if (couple.girl.userData.leftArm) {
        couple.girl.userData.leftArm.rotation.set(-0.35, 0.1, 0.45);
      }
    } else if (pose === 'hug') {
      if (couple.boy.userData.rose) {
        couple.boy.userData.rose.visible = false;
      }

      // Authentic 3D Classic Motorcycle / Bike Model (Facing +X towards Meera)
      const bike = new THREE.Group();

      // Materials
      const paintMat = new THREE.MeshStandardMaterial({
        color: 0xC71F37,
        roughness: 0.25,
        metalness: 0.5
      });
      const chromeMat = new THREE.MeshStandardMaterial({
        color: 0xF0F0F0,
        metalness: 0.95,
        roughness: 0.1
      });
      const engineMat = new THREE.MeshStandardMaterial({
        color: 0x2E2E2E,
        roughness: 0.45,
        metalness: 0.6
      });
      const tireMat = new THREE.MeshStandardMaterial({
        color: 0x161616,
        roughness: 0.85
      });
      const leatherMat = new THREE.MeshStandardMaterial({
        color: 0x381C0E,
        roughness: 0.55
      });
      const lampMat = new THREE.MeshStandardMaterial({
        color: 0xFFF3B0,
        emissive: 0xFFE066,
        emissiveIntensity: 0.95,
        roughness: 0.1
      });
      const tailLightMat = new THREE.MeshStandardMaterial({
        color: 0xFF1E38,
        emissive: 0xD6001C,
        emissiveIntensity: 0.8
      });

      // Helper for authentic spoke wheel
      const createWheel = (posX) => {
        const wGroup = new THREE.Group();
        // Rubber Torus Tire
        const tireGeo = new THREE.TorusGeometry(0.22, 0.05, 12, 24);
        const tire = new THREE.Mesh(tireGeo, tireMat);
        wGroup.add(tire);

        // Chrome Rim & Hub
        const rimGeo = new THREE.CylinderGeometry(0.18, 0.18, 0.04, 16);
        rimGeo.rotateX(Math.PI / 2);
        const rim = new THREE.Mesh(rimGeo, chromeMat);
        wGroup.add(rim);

        // Center Hub Axle
        const hubGeo = new THREE.SphereGeometry(0.06, 8, 8);
        const hub = new THREE.Mesh(hubGeo, chromeMat);
        wGroup.add(hub);

        wGroup.position.set(posX, 0.24, 0);
        return wGroup;
      };

      // 1. Front and Back Wheels
      const frontWheel = createWheel(0.52);
      bike.add(frontWheel);

      const backWheel = createWheel(-0.52);
      bike.add(backWheel);

      // 2. Front Mudguard & Telescopic Forks
      const frontFenderGeo = new THREE.CylinderGeometry(0.25, 0.25, 0.10, 16, 1, false, 0, Math.PI * 0.7);
      frontFenderGeo.rotateZ(-Math.PI * 0.35);
      const frontFender = new THREE.Mesh(frontFenderGeo, paintMat);
      frontFender.position.set(0.52, 0.25, 0);
      bike.add(frontFender);

      // Left & Right Chrome Forks
      const forkGeo = new THREE.CylinderGeometry(0.016, 0.016, 0.54, 8);
      const leftFork = new THREE.Mesh(forkGeo, chromeMat);
      leftFork.position.set(0.48, 0.44, 0.06);
      leftFork.rotation.z = -0.22;
      const rightFork = new THREE.Mesh(forkGeo, chromeMat);
      rightFork.position.set(0.48, 0.44, -0.06);
      rightFork.rotation.z = -0.22;
      bike.add(leftFork);
      bike.add(rightFork);

      // 3. Engine Block & Transmission
      const engineGeo = new THREE.BoxGeometry(0.32, 0.22, 0.20);
      const engine = new THREE.Mesh(engineGeo, engineMat);
      engine.position.set(0.02, 0.28, 0);
      bike.add(engine);

      // Engine Cylinder Head
      const cylGeo = new THREE.CylinderGeometry(0.09, 0.09, 0.14, 12);
      const cyl = new THREE.Mesh(cylGeo, chromeMat);
      cyl.position.set(0.06, 0.40, 0);
      cyl.rotation.z = 0.2;
      bike.add(cyl);

      // 4. Teardrop Fuel Tank
      const tankGeo = new THREE.SphereGeometry(0.20, 14, 10);
      tankGeo.scale(1.4, 0.85, 0.85);
      const tank = new THREE.Mesh(tankGeo, paintMat);
      tank.position.set(0.20, 0.52, 0);
      bike.add(tank);

      // Chrome Gas Cap
      const capGeo = new THREE.CylinderGeometry(0.035, 0.035, 0.02, 8);
      const cap = new THREE.Mesh(capGeo, chromeMat);
      cap.position.set(0.20, 0.61, 0);
      bike.add(cap);

      // 5. Classic Leather Saddle Seat
      const seatGeo = new THREE.BoxGeometry(0.42, 0.08, 0.22);
      const seat = new THREE.Mesh(seatGeo, leatherMat);
      seat.position.set(-0.16, 0.46, 0);
      seat.rotation.z = -0.06;
      bike.add(seat);

      // Rear Fender
      const rearFenderGeo = new THREE.CylinderGeometry(0.25, 0.25, 0.11, 16, 1, false, 0, Math.PI * 0.7);
      rearFenderGeo.rotateZ(Math.PI * 0.65);
      const rearFender = new THREE.Mesh(rearFenderGeo, paintMat);
      rearFender.position.set(-0.52, 0.25, 0);
      bike.add(rearFender);

      // Red Tail Light
      const tailLightGeo = new THREE.SphereGeometry(0.04, 8, 8);
      const tailLight = new THREE.Mesh(tailLightGeo, tailLightMat);
      tailLight.position.set(-0.68, 0.36, 0);
      bike.add(tailLight);

      // 6. Chrome Exhaust Pipe & Silencer
      const pipeGeo = new THREE.CylinderGeometry(0.022, 0.022, 0.68, 8);
      pipeGeo.rotateZ(Math.PI / 2);
      const exhaust = new THREE.Mesh(pipeGeo, chromeMat);
      exhaust.position.set(-0.15, 0.16, 0.12);
      bike.add(exhaust);

      // Silencer Cone Tip
      const tipGeo = new THREE.CylinderGeometry(0.032, 0.022, 0.15, 8);
      tipGeo.rotateZ(Math.PI / 2);
      const tip = new THREE.Mesh(tipGeo, chromeMat);
      tip.position.set(-0.52, 0.16, 0.12);
      bike.add(tip);

      // 7. Handlebars & Cockpit
      const barGeo = new THREE.CylinderGeometry(0.016, 0.016, 0.44, 8);
      const bar = new THREE.Mesh(barGeo, chromeMat);
      bar.position.set(0.42, 0.68, 0);
      bike.add(bar);

      // Big Classic Chrome Headlamp
      const lampGeo = new THREE.SphereGeometry(0.09, 12, 10);
      lampGeo.scale(0.85, 1.0, 1.0);
      const headlamp = new THREE.Mesh(lampGeo, lampMat);
      headlamp.position.set(0.55, 0.62, 0);
      bike.add(headlamp);

      // Dual Chrome Round Rearview Mirrors
      const mirrorGeo = new THREE.SphereGeometry(0.035, 8, 6);
      const leftMirror = new THREE.Mesh(mirrorGeo, chromeMat);
      leftMirror.position.set(0.42, 0.76, 0.18);
      const rightMirror = new THREE.Mesh(mirrorGeo, chromeMat);
      rightMirror.position.set(0.42, 0.76, -0.18);
      bike.add(leftMirror);
      bike.add(rightMirror);

      // Kickstand
      const standGeo = new THREE.CylinderGeometry(0.014, 0.014, 0.22, 6);
      standGeo.rotateZ(0.4);
      const kickstand = new THREE.Mesh(standGeo, chromeMat);
      kickstand.position.set(-0.12, 0.10, 0.14);
      bike.add(kickstand);

      bike.userData = { frontWheel, backWheel };
      bike.scale.set(1.05, 1.05, 1.05);
      bike.position.set(-0.95, 0, 0);
      scene.add(bike);
      couple.scooterProp = bike;

      // Initial character placement
      couple.boy.position.set((-0.95 - 0.20) / 0.78, 0.32, 0);
      couple.boy.rotation.set(0, Math.PI / 2, 0.12);
      if (couple.boy.children[3] && couple.boy.children[4]) {
        couple.boy.children[3].rotation.x = -1.25;
        couple.boy.children[4].rotation.x = -1.25;
      }
      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-1.20, 0, -0.16);
        couple.boy.userData.rightArm.scale.set(1.0, 1.25, 1.0);
      }
      if (couple.boy.userData.leftArm) {
        couple.boy.userData.leftArm.rotation.set(-1.20, 0, 0.16);
        couple.boy.userData.leftArm.scale.set(1.0, 1.25, 1.0);
      }

      couple.girl.position.set(0.42, 0, 0);
      couple.girl.rotation.set(0, -0.55, 0);

      // 3D Romantic Kiss Shield Heart (pops up in front of faces when they kiss!)
      const heartShape = new THREE.Shape();
      const hx = 0, hy = 0;
      heartShape.moveTo(hx + 0.25, hy + 0.25);
      heartShape.bezierCurveTo(hx + 0.25, hy + 0.25, hx + 0.20, hy, hx, hy);
      heartShape.bezierCurveTo(hx - 0.30, hy, hx - 0.30, hy + 0.35, hx - 0.30, hy + 0.35);
      heartShape.bezierCurveTo(hx - 0.30, hy + 0.55, hx - 0.10, hy + 0.77, hx + 0.25, hy + 0.95);
      heartShape.bezierCurveTo(hx + 0.60, hy + 0.77, hx + 0.80, hy + 0.55, hx + 0.80, hy + 0.35);
      heartShape.bezierCurveTo(hx + 0.80, hy + 0.35, hx + 0.80, hy, hx + 0.50, hy);
      heartShape.bezierCurveTo(hx + 0.35, hy, hx + 0.25, hy + 0.25, hx + 0.25, hy + 0.25);

      const extrudeSettings = {
        depth: 0.12,
        bevelEnabled: true,
        bevelSegments: 4,
        steps: 2,
        bevelSize: 0.04,
        bevelThickness: 0.04
      };

      const heartGeo = new THREE.ExtrudeGeometry(heartShape, extrudeSettings);
      heartGeo.center();
      heartGeo.rotateZ(Math.PI);

      const shieldMat = new THREE.MeshStandardMaterial({
        color: 0xFF1E4D,
        emissive: 0xEE1144,
        emissiveIntensity: 0.65,
        roughness: 0.2,
        metalness: 0.3
      });

      const shieldHeart = new THREE.Mesh(heartGeo, shieldMat);
      shieldHeart.scale.set(0.0001, 0.0001, 0.0001);
      shieldHeart.position.set(0, 1.04, 0.62);
      scene.add(shieldHeart);
      couple.kissShieldHeart = shieldHeart;
    } else if (pose === 'celebrate') {
      if (couple.boy.userData.rose) {
        couple.boy.userData.rose.visible = false;
      }

      // Hide default straight cylinder legs for custom articulated kneeling limbs
      if (couple.boy.children[3]) couple.boy.children[3].visible = false;
      if (couple.boy.children[4]) couple.boy.children[4].visible = false;

      // 1. Aarav Kneeling Pose on One Knee
      couple.boy.position.set(-0.45, 0, 0.02);
      couple.boy.rotation.set(0.04, 0.60, 0);

      const pantsMat = new THREE.MeshStandardMaterial({
        color: 0x383432, // Charcoal Tailored Trousers
        roughness: 0.8,
        metalness: 0.1
      });
      const shoeMat = new THREE.MeshStandardMaterial({
        color: 0x1A1416, // Black Leather Oxfords
        roughness: 0.5,
        metalness: 0.2
      });

      const kneelingLegs = new THREE.Group();

      // --- Left Leg (Forward Bent Knee 90° - Firmly Attached to Hip) ---
      const leftLegGroup = new THREE.Group();
      leftLegGroup.position.set(-0.16, 0.40, 0); // attached directly to bottom of sweater/hip

      // Left Thigh (Horizontal forward)
      const leftThighGeo = new THREE.CylinderGeometry(0.12, 0.11, 0.34, 14);
      leftThighGeo.rotateX(Math.PI / 2);
      const leftThigh = new THREE.Mesh(leftThighGeo, pantsMat);
      leftThigh.position.set(0, -0.02, 0.17);
      leftLegGroup.add(leftThigh);

      // Left Knee Joint
      const leftKneeGeo = new THREE.SphereGeometry(0.12, 12, 10);
      const leftKnee = new THREE.Mesh(leftKneeGeo, pantsMat);
      leftKnee.position.set(0, -0.02, 0.34);
      leftLegGroup.add(leftKnee);

      // Left Shin (Vertical down to floor)
      const leftShinGeo = new THREE.CylinderGeometry(0.11, 0.10, 0.34, 14);
      const leftShin = new THREE.Mesh(leftShinGeo, pantsMat);
      leftShin.position.set(0, -0.19, 0.34);
      leftLegGroup.add(leftShin);

      // Left Shoe (Flat on pedestal)
      const leftShoeGeo = new THREE.BoxGeometry(0.14, 0.08, 0.22);
      const leftShoe = new THREE.Mesh(leftShoeGeo, shoeMat);
      leftShoe.position.set(0, -0.36, 0.38);
      leftLegGroup.add(leftShoe);

      kneelingLegs.add(leftLegGroup);

      // --- Right Leg (Knee Resting on Pedestal, Shin Back) ---
      const rightLegGroup = new THREE.Group();
      rightLegGroup.position.set(0.16, 0.40, 0); // attached directly to bottom of sweater/hip

      // Right Thigh (Angled down from hip to floor)
      const rightThighGeo = new THREE.CylinderGeometry(0.12, 0.11, 0.38, 14);
      rightThighGeo.rotateX(0.75);
      const rightThigh = new THREE.Mesh(rightThighGeo, pantsMat);
      rightThigh.position.set(0, -0.16, -0.12);
      rightLegGroup.add(rightThigh);

      // Right Knee (Resting on floor)
      const rightKneeGeo = new THREE.SphereGeometry(0.12, 12, 10);
      const rightKnee = new THREE.Mesh(rightKneeGeo, pantsMat);
      rightKnee.position.set(0, -0.32, -0.24);
      rightLegGroup.add(rightKnee);

      // Right Shin (Extending back along floor)
      const rightShinGeo = new THREE.CylinderGeometry(0.11, 0.10, 0.36, 14);
      rightShinGeo.rotateX(Math.PI / 2);
      const rightShin = new THREE.Mesh(rightShinGeo, pantsMat);
      rightShin.position.set(0, -0.34, -0.42);
      rightLegGroup.add(rightShin);

      // Right Shoe (Toe flexed on ground)
      const rightShoeGeo = new THREE.BoxGeometry(0.14, 0.08, 0.22);
      const rightShoe = new THREE.Mesh(rightShoeGeo, shoeMat);
      rightShoe.position.set(0, -0.34, -0.60);
      rightLegGroup.add(rightShoe);

      kneelingLegs.add(rightLegGroup);
      couple.boy.add(kneelingLegs);
      couple.kneelingLegs = kneelingLegs;

      // 2. Velvet Ring Box with Sparkling 3D Diamond Ring (Soft, Elegant Lighting - No Harsh Glare)
      const ringBoxGroup = new THREE.Group();

      const boxMat = new THREE.MeshStandardMaterial({
        color: 0x6B0D18, // Royal Velvet Crimson
        roughness: 0.75,
        metalness: 0.1
      });
      const goldMat = new THREE.MeshStandardMaterial({
        color: 0xF5C767,
        metalness: 0.95,
        roughness: 0.15
      });
      const diamondMat = new THREE.MeshStandardMaterial({
        color: 0xFFFFFF,
        emissive: 0xCCE5FF,
        emissiveIntensity: 0.25, // Soft elegant glint, no harsh washout
        metalness: 0.2,
        roughness: 0.05
      });

      // Bottom box half
      const boxBaseGeo = new THREE.BoxGeometry(0.14, 0.07, 0.14);
      const boxBase = new THREE.Mesh(boxBaseGeo, boxMat);
      ringBoxGroup.add(boxBase);

      // Top box lid angled open
      const boxLidGeo = new THREE.BoxGeometry(0.14, 0.04, 0.14);
      const boxLid = new THREE.Mesh(boxLidGeo, boxMat);
      boxLid.position.set(0, 0.06, -0.07);
      boxLid.rotation.x = -1.2;
      ringBoxGroup.add(boxLid);

      // Gold Ring Band
      const bandGeo = new THREE.TorusGeometry(0.04, 0.008, 10, 20);
      const band = new THREE.Mesh(bandGeo, goldMat);
      band.position.set(0, 0.04, 0);
      ringBoxGroup.add(band);

      // Sparkling Solitaire Diamond Gem
      const diamondGeo = new THREE.OctahedronGeometry(0.03, 0);
      const diamond = new THREE.Mesh(diamondGeo, diamondMat);
      diamond.position.set(0, 0.085, 0);
      ringBoxGroup.add(diamond);

      ringBoxGroup.scale.set(1.4, 1.4, 1.4);
      ringBoxGroup.position.set(0.04, -0.52, 0.12);
      ringBoxGroup.rotation.set(-0.25, -0.15, -0.35);
      
      if (couple.boy.userData.leftArm) {
        couple.boy.userData.leftArm.add(ringBoxGroup);
        // Left arm extends forward holding the velvet box
        couple.boy.userData.leftArm.rotation.set(-1.18, 0.28, 0.40);
      }
      couple.ringBoxProp = ringBoxGroup;
      couple.diamondMesh = diamond;

      // Right arm extends to support the ring box with both hands in devotion
      if (couple.boy.userData.rightArm) {
        couple.boy.userData.rightArm.rotation.set(-1.15, -0.28, -0.38);
      }
      if (couple.boy.userData.head) {
        couple.boy.userData.head.rotation.set(-0.18, 0.48, 0.06); // looking up at Meera
      }

      // 3. Meera standing with both hands placed gracefully on her blushing cheeks in overjoyed delight!
      couple.girl.position.set(0.45, 0, 0.05);
      couple.girl.rotation.set(0.04, -0.58, 0);

      if (couple.girl.userData.rightArm) {
        couple.girl.userData.rightArm.rotation.set(-1.52, 0.22, -0.28);
      }
      if (couple.girl.userData.leftArm) {
        couple.girl.userData.leftArm.rotation.set(-1.52, -0.22, 0.28);
      }
      if (couple.girl.userData.head) {
        couple.girl.userData.head.rotation.set(0.20, -0.52, -0.05); // looking down at Aarav warmly
      }
    }
  }

  triggerCelebration() {
    if (this.scenes['celebrate']) {
      this.scenes['celebrate'].isCelebrated = true;
      this.scenes['celebrate'].celebrationTime = 0;
    }
  }
}
